import * as React from 'react';
import { dismissModal, openTaxExemptModal } from '@/redux/modules/modal';
import { getCertificatesHaveLoaded, getHasPendingOrCompleteCertificate } from '@/redux/modules/certCapture';
import { getOpenModal } from '@/redux/modules/modal.selectors';
import { getTaxCertManagementUrl } from '@/utils/urls';
import { modalTypes } from '@/enums';
import { parseQueryParams } from '@/utils/queryParams';
import { sendResellerType } from '@/redux/modules/reseller';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { useIntl } from '@liveauctioneers/hammer-ui-core/intl';
import { useLocation, useNavigate } from 'react-router-dom';
import checkForSkippablePage from './helpers/checkForSkippablePage';
import ConfirmModal from '@liveauctioneers/caterwaul-components/lib/SimpleModal/ConfirmModal/ConfirmModal';

// treat going to the tax-exempt page as if they had seen the modal
const isTaxExemptPage = (path: string = '', tab: string) =>
    path.includes('dashboard/profile') && tab === 'tax-cert-management';

type QueryParams = {
    tab: string;
};

const TaxExemptModal = () => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const { pathname, search } = useLocation();
    const [show, setShow] = React.useState(true);
    const { formatMessage } = useIntl();
    const { tab } = parseQueryParams<QueryParams>(search);
    const taxExemptPage = isTaxExemptPage(pathname, tab);
    const isSkippablePage = checkForSkippablePage(pathname);

    const [modalPopped, setModalPopped] = React.useState(false);
    const isModalOpen = useAppSelector(getOpenModal) === modalTypes.TAX_EXEMPT;
    const hasValidTaxExemptCertificate = useAppSelector(getHasPendingOrCompleteCertificate);
    const certificatesHaveLoaded = useAppSelector(getCertificatesHaveLoaded);

    const automaticallyPopModal = () => {
        dispatch(openTaxExemptModal());
        setModalPopped(true);
    };

    const handleDismiss = React.useCallback(() => {
        setShow(false);
    }, []);

    // If they go to the TaxExempt page don't pop the modal again during a session
    React.useEffect(() => {
        if (taxExemptPage) {
            setModalPopped(true);
        }
    }, [taxExemptPage]);

    React.useEffect(() => {
        if (
            certificatesHaveLoaded &&
            !hasValidTaxExemptCertificate &&
            !modalPopped &&
            !taxExemptPage &&
            !isSkippablePage
        ) {
            window.setTimeout(automaticallyPopModal, 2000);
        }
    }, [certificatesHaveLoaded, hasValidTaxExemptCertificate, isSkippablePage, modalPopped, taxExemptPage]); // eslint-disable-line

    if (!isModalOpen) {
        return null;
    }

    return (
        <ConfirmModal
            cancelText={formatMessage({ id: 'taxExemptModal.notExempt' })}
            confirmText={formatMessage({
                id: 'taxExemptModal.upload',
            })}
            onAfterClose={() => dispatch(dismissModal())}
            onConfirm={() => {
                handleDismiss();
                navigate(getTaxCertManagementUrl());
            }}
            onDismiss={() => {
                dispatch(sendResellerType(0));
                handleDismiss();
            }}
            show={show}
            testid="TwoStepAuthenticationActivationModal"
            title={formatMessage({
                id: 'taxExemptModal.title',
            })}
            warning={formatMessage({
                id: 'taxExemptModal.subtitle',
            })}
        />
    );
};

export default TaxExemptModal;
